import { useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { endOfDay, format, startOfDay, subMonths } from 'date-fns'

import { ChartBarIcon } from '@gto/icons-react/outline/chart-bar'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { PresentationChartLineIcon } from '@gto/icons-react/outline/presentation-chart-line'
import { ShoppingCartIcon } from '@gto/icons-react/outline/shopping-cart'
import { TableCellsIcon } from '@gto/icons-react/outline/table-cells'
import { UserGroupIcon } from '@gto/icons-react/outline/user-group'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { DateRangePicker } from '@gto/ui/date-picker'
import { ToggleButton, ToggleButtonGroup } from '@gto/ui/toggle-button'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAnalyticsRowSchema } from '@/api/gen'
import { useGetMyAnalytics } from '@/api/gen'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { DataTable } from '@/components/data-table'
import { InlineQueryError } from '@/components/inline-query-error'
import { Page } from '@/components/page'
import { PerformanceChart } from '@/components/performance-chart'
import { StatsCard } from '@/components/stats-card'
import { fluidColumn } from '@/components/table-columns'
import { useDateShortcuts } from '@/hooks/use-date-shortcuts'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { toAmount } from '@/utils/to-amount'

// Reports has a wider union than the dashboard's shared `Granularity` (it adds
// 'yearly'), so the type and predicate stay local rather than force a merged
// 4-value type onto the simpler surfaces.
type ReportGranularity = 'daily' | 'weekly' | 'monthly' | 'yearly'

const REPORT_GRANULARITIES = ['daily', 'weekly', 'monthly', 'yearly'] as const
const isReportGranularity = (v: string): v is ReportGranularity =>
  (REPORT_GRANULARITIES as readonly string[]).includes(v)

const toDateStr = (d: Date) => format(d, 'yyyy-MM-dd')

type ReportRow = AffiliateAnalyticsRowSchema & { id: string }

const useReportColumns = (): ColumnDef<ReportRow>[] => {
  const { formatMessage } = useIntl()

  return createColumns<ReportRow>([
    {
      accessorKey: 'period_label',
      header: formatMessage({ id: 'reports.period' }),
      ...fluidColumn({ min: 140, flex: 1 }),
      enableSorting: false,
      cell: ({ getValue }) => String(getValue()),
    },
    {
      accessorKey: 'orders',
      header: formatMessage({ id: 'reports.orders' }),
      ...fluidColumn({ min: 80, flex: 0.5, align: 'right' }),
      enableSorting: false,
      cell: ({ getValue }) => <FormattedNumber value={Number(getValue() ?? 0)} />,
    },
    {
      accessorKey: 'unique_customers',
      header: formatMessage({ id: 'reports.customers' }),
      ...fluidColumn({ min: 100, flex: 0.6, align: 'right' }),
      enableSorting: false,
      cell: ({ getValue }) => <FormattedNumber value={Number(getValue() ?? 0)} />,
    },
    {
      accessorKey: 'registrations',
      header: formatMessage({ id: 'reports.registrations' }),
      ...fluidColumn({ min: 100, flex: 0.6, align: 'right' }),
      enableSorting: false,
      cell: ({ getValue }) => <FormattedNumber value={Number(getValue() ?? 0)} />,
    },
    {
      accessorKey: 'revenue_usd',
      header: formatMessage({ id: 'reports.revenue' }),
      ...fluidColumn({ min: 100, flex: 0.7, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => (
        <FormattedNumber
          currency="USD"
          maximumFractionDigits={0}
          style="currency"
          value={toAmount(row.original.revenue_usd, 'reports table revenue_usd:')}
        />
      ),
    },
    {
      accessorKey: 'commission_usd',
      header: formatMessage({ id: 'reports.commissions' }),
      ...fluidColumn({ min: 100, flex: 0.7, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => (
        <Typography
          color="primary"
          variant="body2"
        >
          <FormattedNumber
            currency="USD"
            maximumFractionDigits={0}
            style="currency"
            value={toAmount(row.original.commission_usd, 'reports table commission_usd:')}
          />
        </Typography>
      ),
    },
  ])
}

export const ReportsPage = () => {
  const { formatMessage } = useIntl()
  const shortcuts = useDateShortcuts()

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => [
    subMonths(new Date(), 1),
    new Date(),
  ])
  const [granularity, setGranularity] = useState<ReportGranularity>('daily')

  const [from, to] = dateRange
  const queryParams = {
    date_from: from ? toDateStr(from) : toDateStr(subMonths(new Date(), 1)),
    date_to: to ? toDateStr(to) : toDateStr(new Date()),
    granularity,
  }

  const analyticsQuery = useGetMyAnalytics(queryParams)
  const { data: analytics, isLoading, isError, refetch } = analyticsQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  useReportQueryError(analyticsQuery, 'getMyAnalytics failed: ')

  // Analytics rows arrive newest-first; charts and sparklines read
  // chronologically (oldest on the left), so reverse a copy here.
  const analyticsRows = [...(analytics?.rows ?? [])].reverse()
  const ordersSeries = analyticsRows.map((row) => row.orders)
  const customersSeries = analyticsRows.map((row) => row.unique_customers)
  const revenueSeries = analyticsRows.map((row) =>
    toAmount(row.revenue_usd, 'reports analytics revenue_usd:'),
  )
  const commissionsSeries = analyticsRows.map((row) =>
    toAmount(row.commission_usd, 'reports analytics commission_usd:'),
  )

  const totals = analytics?.totals
  const totalRevenue = toAmount(totals?.revenue_usd, 'reports totals revenue_usd:')
  const totalCommissions = toAmount(totals?.commission_usd, 'reports totals commission_usd:')

  const chartData = analyticsRows.map((row) => ({
    label: row.period_label,
    revenue: toAmount(row.revenue_usd, 'reports analytics revenue_usd:'),
    commissions: toAmount(row.commission_usd, 'reports analytics commission_usd:'),
  }))

  const columns = useReportColumns()
  // Table reads most-recent-first, so keep the API order (cache array untouched).
  const rows: ReportRow[] = (analytics?.rows ?? []).map((row) => ({
    ...row,
    id: `${row.period_start}-${row.period_label}`,
  }))

  const granularityToggle = (
    <ToggleButtonGroup
      size="small"
      value={granularity}
      onValueChange={(v) => {
        if (isReportGranularity(v)) {
          setGranularity(v)
        }
      }}
    >
      <ToggleButton value="daily">
        {formatMessage({ id: 'dashboard.granularity.daily' })}
      </ToggleButton>
      <ToggleButton value="weekly">
        {formatMessage({ id: 'dashboard.granularity.weekly' })}
      </ToggleButton>
      <ToggleButton value="monthly">
        {formatMessage({ id: 'dashboard.granularity.monthly' })}
      </ToggleButton>
      <ToggleButton value="yearly">
        {formatMessage({ id: 'dashboard.granularity.yearly' })}
      </ToggleButton>
    </ToggleButtonGroup>
  )

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage id="reports.title" />}
        actions={
          <DateRangePicker
            closeOnSelect={false}
            presets={shortcuts}
            value={dateRange}
            onChange={([newFrom, newTo], context) => {
              if (context.validationError.some(Boolean)) {
                return
              }
              setDateRange([newFrom ? startOfDay(newFrom) : null, newTo ? endOfDay(newTo) : null])
            }}
          />
        }
      />

      <Page.Content>
        {isError ? (
          <InlineQueryError
            data-tst="reports-error"
            messageId="reports.error"
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            <div
              className="flex flex-wrap gap-2"
              data-tst="kpi-strip"
            >
              <DashboardKpiTile
                icon={<ShoppingCartIcon />}
                label={<FormattedMessage id="reports.totalOrders" />}
                loading={isLoading}
                sparklineData={ordersSeries}
                tooltip={formatMessage({ id: 'tooltip.totalOrders' })}
                value={<FormattedNumber value={totals?.orders ?? 0} />}
              />
              <DashboardKpiTile
                icon={<UserGroupIcon />}
                label={<FormattedMessage id="reports.totalCustomers" />}
                loading={isLoading}
                sparklineData={customersSeries}
                tooltip={formatMessage({ id: 'tooltip.uniqueCustomers' })}
                value={<FormattedNumber value={totals?.unique_customers ?? 0} />}
              />
              <DashboardKpiTile
                icon={<CurrencyDollarIcon />}
                label={<FormattedMessage id="reports.totalRevenue" />}
                loading={isLoading}
                sparklineData={revenueSeries}
                tooltip={formatMessage({ id: 'tooltip.totalRevenue' })}
                value={
                  <FormattedNumber
                    currency="USD"
                    maximumFractionDigits={0}
                    style="currency"
                    value={totalRevenue}
                  />
                }
              />
              <DashboardKpiTile
                icon={<ChartBarIcon />}
                label={<FormattedMessage id="reports.totalCommissions" />}
                loading={isLoading}
                sparklineData={commissionsSeries}
                tooltip={formatMessage({ id: 'tooltip.myCommissions' })}
                value={
                  <FormattedNumber
                    currency="USD"
                    maximumFractionDigits={0}
                    style="currency"
                    value={totalCommissions}
                  />
                }
              />
            </div>

            <PerformanceChart
              controls={granularityToggle}
              data={chartData}
              icon={<PresentationChartLineIcon />}
              title={<FormattedMessage id="dashboard.performance" />}
              totals={[
                {
                  id: 'revenue',
                  label: <FormattedMessage id="admin.dashboard.revenueThisPeriod" />,
                  value: (
                    <FormattedNumber
                      currency="USD"
                      maximumFractionDigits={0}
                      style="currency"
                      value={totalRevenue}
                    />
                  ),
                },
                {
                  id: 'commissions',
                  label: <FormattedMessage id="admin.dashboard.commissionsThisPeriod" />,
                  value: (
                    <FormattedNumber
                      currency="USD"
                      maximumFractionDigits={0}
                      style="currency"
                      value={totalCommissions}
                    />
                  ),
                },
              ]}
            />

            <StatsCard
              icon={<TableCellsIcon />}
              title={<FormattedMessage id="reports.detailedBreakdown" />}
            >
              <DataTable
                columns={columns}
                data={rows}
                // A period report is read as one list, so the pager only appears past 50 rows.
                // `pageSizeOptions` has to say so too: the pager shows when the rows exceed the
                // SMALLEST offered size, so leaving the default [10, 25, 50] here put an inert
                // pager and a rows-per-page picker under every 11-to-50-row report.
                defaultPageSize={50}
                getRowId={(row: ReportRow) => row.id}
                loading={isLoading}
                pageSizeOptions={[50]}
              />
            </StatsCard>
          </>
        )}
      </Page.Content>
    </Page>
  )
}
